import { Box, Center, Flex, Group, SegmentedControl } from '@mantine/core';
import HeaderBreadCrumbs from '@/layouts/AutomationPageWrapper/Header/HeaderBreadCrumbs.tsx';
import VersionButton from '@/pages/Editor/components/VersionHistory/VersionButton.tsx';
import {
  HiOutlineCheckCircle,
  HiOutlineCog6Tooth,
  HiOutlineRectangleStack,
  HiOutlineSparkles,
} from 'react-icons/hi2';
import AppIcon from '@/components/AppIcon.tsx';
import { useLocation, useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import React, { useEffect, useMemo, useState } from 'react';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import { useViewChatSidebar } from '@/layouts/AutomationPageWrapper/hooks/useViewChatSidebar.ts';
import useLatestAutomationRun from '@/hooks/api/runs/useLatestAutomationRun.ts';
import ActiveStatusSpinner from '@/components/ActiveStatusSpinner.tsx';
import { ActiveStatuses } from '@/types/statuses.ts';
import { Automation } from '@/types/automations.ts';

type props = {
  automation: Automation;
};

const SegmentControlStyles = {
  indicator: {
    boxShadow:
      'lch(0 0 0 / 0.02) 0px 6px 18px, lch(0 0 0 / 0.04) 0px 3px 9px, lch(0 0 0 / 0.04) 0px 1px 1px',
  },
  control: {
    '--separator-color': 'transparent',
  },
};

const AutomationHeader = ({ automation }: props) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { showChat, setShowChat } = useViewChatSidebar();

  const isAutomationPage = !!useMatch('/automations/:automationId');
  const isEditorPage = !!useMatch('/automations/:automationId/editor');
  const isRunPage = !!useMatch('/automations/:automationId/runs/*');
  const isResultPage = !!useMatch('/automations/:automationId/results/*');

  const [previousRunUrl, setPreviousRunUrl] = useState<string | null>(null);
  const [previousResultUrl, setPreviousResultUrl] = useState<string | null>(null);

  const runId = searchParams.get('runId');

  const location = useLocation();

  useEffect(() => {
    const url = location.pathname + location.search + location.hash;
    if (isRunPage) {
      setPreviousRunUrl(url);
    } else if (isResultPage) {
      setPreviousResultUrl(url);
    }
  }, [isResultPage, isRunPage, location.hash, location.pathname, location.search, runId]);

  useEffect(() => {
    if (isAutomationPage) {
      setPreviousResultUrl(null);
    }
  }, [isAutomationPage]);

  const shouldFetchLatestRun = !automation.latest_run_id && automation.run_count > 0;
  const { latestRun } = useLatestAutomationRun({ enabled: shouldFetchLatestRun });

  const latestRunId = automation.latest_run_id || latestRun?.id;

  const lastRunId = useMemo(() => runId || latestRunId, [runId, latestRunId]);

  const latestRunStatus = latestRun?.status || automation.latest_run_status;
  const latestRunIsRunning = latestRunStatus && ActiveStatuses.includes(latestRunStatus);

  const [value, setValue] = useState('results');

  useEffect(() => {
    if (isEditorPage) {
      setValue('editor');
    } else if (isRunPage) {
      setValue('runs');
    } else if (isResultPage || isAutomationPage) {
      setValue('results');
    }
  }, [isAutomationPage, isEditorPage, isResultPage, isRunPage]);

  return (
    <Box
      component="header"
      px="md"
      mih={55}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0',
        width: '100%',
        gap: 8,
      }}
    >
      <Group w="100%" wrap="nowrap" style={{ minWidth: 0 }} pr={'lg'}>
        {!isAutomationPage && <HeaderBreadCrumbs automation={automation} />}
      </Group>

      <Center style={{ justifySelf: 'center' }}>
        <SegmentedControl
          value={value}
          onChange={(newValue: string) => {
            if (newValue === 'editor') {
              navigate(`/automations/${automation.id}/editor`);
            } else if (newValue === 'runs') {
              if (lastRunId) {
                navigate(previousRunUrl || `/automations/${automation.id}/runs/${lastRunId}`);
              } else {
                navigate(`/automations/${automation.id}/runs`);
              }
            } else if (newValue === 'results' && previousResultUrl) {
              navigate(previousResultUrl);
            } else {
              navigate(`/automations/${automation.id}`);
            }
          }}
          styles={SegmentControlStyles}
          w={280}
          radius="xl"
          data={[
            {
              value: 'editor',
              label: (
                <Center style={{ gap: 5 }} w={70}>
                  <AppIcon Icon={HiOutlineCog6Tooth} size={16} />
                  <span style={{ fontWeight: 500 }}>Editor</span>
                </Center>
              ),
            },
            {
              value: 'runs',
              label: (
                <Flex>
                  <Center w={70} style={{ gap: 5 }}>
                    {latestRunIsRunning ? (
                      <ActiveStatusSpinner />
                    ) : (
                      <AppIcon Icon={HiOutlineRectangleStack} size={16} />
                    )}
                    <span style={{ fontWeight: 500 }}>Runs</span>
                  </Center>
                </Flex>
              ),
            },
            {
              value: 'results',
              label: (
                <Center w={70} style={{ gap: 5 }}>
                  <AppIcon Icon={HiOutlineCheckCircle} size={16} />
                  <span style={{ fontWeight: 500 }}>Results</span>
                </Center>
              ),
            },
          ]}
        />
      </Center>

      <Group w="100%" justify="right" style={{ justifySelf: 'end', minWidth: 0 }}>
        {(isRunPage || isResultPage) && !showChat && (
          <StyledButton size="sm" variant="icon" aria-label="Open chat" onClick={() => setShowChat(!showChat)}>
            <AppIcon Icon={HiOutlineSparkles} />
          </StyledButton>
        )}
        {isEditorPage && <VersionButton automation={automation} />}
      </Group>
    </Box>
  );
};

export default AutomationHeader;
